/**
 * Лента поставщика — §5 дополнения о сверке, D264.
 *
 * Одна таблица по датам, новые сверху: накладная — красная, оплата —
 * зелёная (отметка в системе — бледнее), акт сверки — синий, строка акта,
 * которой нет у нас, — бледная «призрачная». Неразобранное расхождение —
 * жёлтая подсветка строки и объяснение словами под ней. Сверху — три
 * долга рядом: в системе, по банку, по последнему акту.
 */

import { useRef, useState } from 'react';
import type { ChangeEvent, ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '@shell/api';
import { getRestaurantTimeZone } from '@shell/format';
import { Empty, ErrorState, Loading } from '@shell/states';
import type { Navigation } from '../../api';
import { rub } from '../../api';
import { Icon } from '../../icons';
import { canSee } from '../../nav';
import { Link, navigate, useRoute } from '../../router';
import { Notice, isoDate } from '../../ui';
import { cardPath } from '../payments/paths';
import { LIVE, amount, reconcileApi, signed } from './api';
import type { ActView, Ledger, LedgerIssue, LedgerRow } from './api';
import { ActBadge } from './Suppliers';
import {
  PERIODS,
  TAB_PATHS,
  actPath,
  ledgerPath,
  ledgerQuery,
  periodRange,
  readPeriod,
  todayIso,
} from './paths';

const KIND_TITLE: Record<LedgerRow['kind'], string> = {
  invoice: 'Накладная',
  bank: 'Оплата из банка',
  mark: 'Отметка оплаты',
  act: 'Акт сверки',
  ghost: 'Строка акта, которой нет у нас',
};

export function hasOpenIssue(row: { issues: LedgerIssue[] }): boolean {
  return row.issues.some((issue) => !issue.resolved);
}

function deferral(supplier: Ledger['supplier']): string {
  if (supplier.settles_immediately) return 'расчёт при получении';
  if (supplier.deferral_days === null) return 'отсрочка не задана';
  const source = supplier.deferral_source ? ` (${supplier.deferral_source})` : '';
  return `отсрочка ${supplier.deferral_days} дн.${source}`;
}

function bankNote(debts: Ledger['debts']): string {
  if (debts.bank === null) return 'выписка не загружена';
  if (debts.bank_anchor === 'act' && debts.act) {
    return `от акта${debts.act.number ? ` № ${debts.act.number}` : ''}${
      debts.act.act_date ? ` на ${isoDate(debts.act.act_date)}` : ''
    }`;
  }
  if (debts.bank_anchor === 'statement' && debts.bank_since) {
    return `долг в системе минус оплаченное по выписке на ${isoDate(debts.bank_since)}`;
  }
  if (debts.bank_since) return `с ${isoDate(debts.bank_since)}`;
  return 'накладные минус платежи из выписки';
}

export function LedgerScreen({ id, nav }: { id: number; nav: Navigation }) {
  const route = useRoute();
  const client = useQueryClient();
  const period = readPeriod(route.params);
  const onlyIssues = route.params.get('only') === 'issues';
  const range = periodRange(period, todayIso(getRestaurantTimeZone()));
  const fileInput = useRef<HTMLInputElement>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const seesPayments = canSee('payments', nav);

  const query = useQuery<Ledger, ApiError>({
    queryKey: ['reconcile', 'ledger', id, range.from, range.to],
    queryFn: () => reconcileApi.ledger(id, range),
    ...LIVE,
    placeholderData: (previous) => (previous?.supplier.id === id ? previous : undefined),
  });

  const uploadAct = useMutation<{ act: ActView }, ApiError, File>({
    mutationFn: (file) => reconcileApi.uploadAct(id, file),
    onSuccess: (answer) => {
      client.setQueryData(['reconcile', 'act', answer.act.id], answer.act);
      client.invalidateQueries({ queryKey: ['reconcile'] });
      navigate(actPath(answer.act.id));
    },
    onError: (error) =>
      setProblem(error.detail ?? 'Акт не загрузился. Попробуйте ещё раз или пришлите другой файл.'),
  });

  const pick = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setProblem(null);
    uploadAct.mutate(file);
  };

  const back = (
    <button type="button" className="rc-back" onClick={() => navigate(TAB_PATHS.suppliers)}>
      <Icon name="back" size={16} /> Все поставщики
    </button>
  );

  if (query.isPending) {
    return (
      <>
        {back}
        <Loading />
      </>
    );
  }
  if (query.error) {
    return (
      <>
        {back}
        {query.error.status === 404 ? (
          <ErrorState title="Поставщика нет" text="Его могли удалить из справочника. Вернитесь к списку." />
        ) : (
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        )}
      </>
    );
  }

  const data = query.data;
  const rows = onlyIssues ? data.rows.filter(hasOpenIssue) : data.rows;
  const openIssues = data.rows.filter(hasOpenIssue).length;
  const setFilter = (next: { period?: typeof period; only?: boolean }) =>
    navigate(
      `${ledgerPath(id)}${ledgerQuery(next.period ?? period, next.only ?? onlyIssues)}`,
      { replace: true },
    );

  return (
    <div className="rc-stack">
      {back}
      <section className="rc-card rc-ledger-head" aria-label="Поставщик">
        <header className="rc-card__head">
          <div>
            <h2 className="rc-card__title">{data.supplier.name}</h2>
            <p className="faint">
              {data.supplier.inn ? `ИНН ${data.supplier.inn}` : 'ИНН не известен'} · {deferral(data.supplier)}
            </p>
          </div>
          <div className="rc-actions">
            {data.can_edit && (
              <>
                <button
                  type="button"
                  className="button button--primary button--small"
                  disabled={uploadAct.isPending}
                  onClick={() => fileInput.current?.click()}
                >
                  {uploadAct.isPending ? 'Читаю акт…' : 'Загрузить акт сверки'}
                </button>
                <input
                  ref={fileInput}
                  type="file"
                  accept=".pdf,.xlsx,.xls"
                  hidden
                  onChange={pick}
                  aria-label="Файл акта сверки"
                />
              </>
            )}
            {seesPayments && (
              <button
                type="button"
                className="button button--small"
                onClick={() => navigate(`/payments/debts?list=supplier&supplier_id=${data.supplier.id}`)}
              >
                Все накладные
              </button>
            )}
          </div>
        </header>
        {problem && <Notice kind="error">{problem}</Notice>}

        <div className="rc-debts">
          <Debt label="Долг в системе" value={rub(data.debts.system)} note="по отметкам «Оплачено»" />
          <Debt
            label="Долг по банку"
            value={data.debts.bank === null ? '—' : rub(data.debts.bank)}
            note={bankNote(data.debts)}
          />
          <Debt
            label="Долг по акту"
            value={data.debts.act ? rub(data.debts.act.value) : '—'}
            note={
              data.debts.act ? (
                <Link to={actPath(data.debts.act.act_id)}>
                  {data.debts.act.act_date ? `на ${isoDate(data.debts.act.act_date)}` : 'открыть акт'}
                </Link>
              ) : (
                'актов не загружали'
              )
            }
          />
        </div>
        {data.debts.hint && <p className="rc-hint">{data.debts.hint}</p>}
        <p className="rc-todo">
          {openIssues > 0
            ? 'Жёлтые строки — расхождения. Откройте строку: под ней написано, что не сходится и что сделать.'
            : data.acts.length === 0
              ? data.can_edit
                ? 'Актов сверки ещё нет. Попросите акт у поставщика и загрузите его — система сверит его с нашим учётом.'
                : 'Актов сверки ещё нет. Загрузить акт может тот, у кого право «Оплаты · поставщики и выписка банка» на правку.'
              : 'Расхождений за период нет.'}
        </p>
      </section>

      {data.acts.length > 0 && (
        <section className="rc-card" aria-label="Акты сверки">
          <h3 className="block-title">Акты сверки</h3>
          <ul className="rc-plain-list rc-acts">
            {data.acts.map((act) => (
              <li key={act.id}>
                <Link to={actPath(act.id)} className="rc-act-link">
                  <span>
                    Акт{act.number ? ` № ${act.number}` : ''}
                    {act.act_date ? ` от ${isoDate(act.act_date)}` : ''}
                    {act.period_from && act.period_to
                      ? ` · период ${isoDate(act.period_from)}–${isoDate(act.period_to)}`
                      : ''}
                  </span>
                  <ActBadge status={act.status} issues={act.issues} />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="rc-card" aria-label="Лента поставщика">
        <div className="rc-ledger-tools">
          <div className="chips" role="group" aria-label="Период">
            {PERIODS.map((item) => (
              <button
                key={item.code}
                type="button"
                className={`chip${item.code === period ? ' chip--active' : ''}`}
                aria-pressed={item.code === period}
                onClick={() => setFilter({ period: item.code })}
              >
                {item.title}
              </button>
            ))}
          </div>
          <label className="rc-check">
            <input
              type="checkbox"
              checked={onlyIssues}
              onChange={(event) => setFilter({ only: event.target.checked })}
            />
            только расхождения
          </label>
        </div>
        <p className="faint rc-period">
          {isoDate(data.period.from)} — {isoDate(data.period.to)}
        </p>
        <ul className="rc-legend" aria-label="Цвета строк">
          <li className="rc-legend__item rc-legend__item--invoice">Накладная</li>
          <li className="rc-legend__item rc-legend__item--payment">Оплата</li>
          <li className="rc-legend__item rc-legend__item--act">Акт сверки</li>
          <li className="rc-legend__item rc-legend__item--issue">Расхождение</li>
        </ul>

        {rows.length === 0 ? (
          <Empty
            title={onlyIssues ? 'Расхождений за период нет' : 'За этот период в ленте пусто'}
            text={
              onlyIssues
                ? 'Снимите галочку «только расхождения», чтобы увидеть все накладные и оплаты.'
                : 'Ни накладных, ни оплат. Выберите период длиннее — «С начала года».'
            }
          />
        ) : (
          <div className="rc-table rc-ledger" role="table" aria-label="Накладные, оплаты и акты">
            <div className="rc-ledger__row rc-table__head" role="row">
              <span role="columnheader">Дата</span>
              <span role="columnheader">Что</span>
              <span role="columnheader" className="num">Сумма, ₽</span>
              <span role="columnheader" className="num">Остаток, ₽</span>
            </div>
            {rows.map((row) => (
              <LedgerLine key={row.key} row={row} seesPayments={seesPayments} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Debt({ label, value, note }: { label: string; value: ReactNode; note: ReactNode }) {
  return (
    <div className="rc-debt">
      <span className="rc-debt__label">{label}</span>
      <span className="rc-debt__value">{value}</span>
      <span className="rc-debt__note">{note}</span>
    </div>
  );
}

function LedgerLine({ row, seesPayments }: { row: LedgerRow; seesPayments: boolean }) {
  const [open, setOpen] = useState(false);
  const issue = hasOpenIssue(row);
  const className = `rc-ledger__row rc-row rc-row--${row.kind}${issue ? ' rc-row--issue' : ''}`;
  const target =
    row.kind === 'invoice' && row.invoice_id !== null && seesPayments
      ? cardPath(row.invoice_id)
      : row.kind === 'act' && row.act_id !== null
        ? actPath(row.act_id)
        : null;

  const cells = (
    <>
      <span role="cell" className="rc-row__date">{isoDate(row.date)}</span>
      <span role="cell" className="rc-row__what">
        <span className="rc-row__title">
          <span className="rc-row__kind">{KIND_TITLE[row.kind]}</span>
          {row.title}
          {row.number ? <span className="rc-row__number"> № {row.number}</span> : null}
        </span>
        {(row.status || row.due) && (
          <span className="faint rc-row__meta">
            {row.status}
            {row.status && row.due ? ' · ' : ''}
            {row.due ? `срок ${isoDate(row.due)}` : ''}
          </span>
        )}
        {row.purpose && (
          <span className={`faint rc-row__purpose${open ? ' rc-row__purpose--open' : ''}`} title={row.purpose}>
            {row.purpose}
          </span>
        )}
      </span>
      <span role="cell" className="num rc-row__amount" data-label="Сумма">
        {signed(row.amount, row.sign)}
      </span>
      <span role="cell" className="num rc-row__balance" data-label="Остаток">
        {row.balance === null ? '' : amount(row.balance)}
      </span>
    </>
  );

  return (
    <div className={className} role="row" data-kind={row.kind}>
      {target ? (
        <Link to={target} className="rc-row__grid">
          {cells}
        </Link>
      ) : (
        <button
          type="button"
          className="rc-row__grid"
          aria-expanded={row.purpose ? open : undefined}
          onClick={() => setOpen((value) => !value)}
        >
          {cells}
        </button>
      )}
      {row.issues.length > 0 && (
        <ul className="rc-row__issues">
          {row.issues.map((item) => (
            <li
              key={item.key}
              className={item.resolved ? 'rc-issue rc-issue--resolved' : 'rc-issue'}
            >
              <span className="rc-issue__text">{item.text}</span>
              {item.resolved && (
                <span className="faint">
                  {' '}
                  Разобрались: {item.resolved.reason}
                  {item.resolved.by ? ` (${item.resolved.by})` : ''}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
